import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Card from '@applied/dragonfruit/src/components/Card/Card';
import Icon from '@applied/dragonfruit/src/components/Icon/Icon';
import Flex from '@applied/dragonfruit/src/components/Layout/Flex';
import type { DisabledTab } from '@applied/dragonfruit/src/components/PageTabs/TabBar';
import TabBar from '@applied/dragonfruit/src/components/PageTabs/TabBar';
import TabbedPage from '@applied/dragonfruit/src/components/PageTabs/TabbedPage';
import Tabs from '@applied/dragonfruit/src/components/PageTabs/tabs';
import TabWindow from '@applied/dragonfruit/src/components/PageTabs/TabWindow';
import { Typography } from '@applied/dragonfruit/src/components/Text/Typography';
import { useQuery } from '@tanstack/react-query';
import { CandidateExperienceQueryKeys } from 'api/query_keys';

import { EmployeePageTab } from 'components/EmployeePage/employee_page_classes';
import { useAppliedLogoSrc } from 'utils/reskin_utils';

import './employee-page.scss';

import { OfferList } from './OfferList/OfferList';
import { PageList } from './PageList/PageList';
import { hasPageScope, PAGES_TAB_DISABLED_TOOLTIP } from './employee_page_utils';
import { getEmployeeInfo } from './employee_requests';

const ENABLE_PAGES = false;

type HeaderProps = {
  employeeName: string;
};

function Header(props: HeaderProps): JSX.Element {
  const { employeeName } = props;
  const logoSrc = useAppliedLogoSrc();
  return (
    <Flex className="header-container" direction="column" alignment="flex-start" gap="md">
      <Flex direction="row" alignment="center" gap="sm">
        <Icon src={logoSrc} height={36} />
        <Typography variant="title">Welcome, {employeeName}!</Typography>
      </Flex>
      <Typography variant="body">
        Access active offers through the &quot;Offers&quot; tab
        {ENABLE_PAGES ? ', and candidate portal pages through the &quot;Pages&quot; tab' : ''}.
      </Typography>
    </Flex>
  );
}

export function EmployeePage(): JSX.Element {
  const history = useHistory();
  const {
    data: employeeInfo,
    isLoading: isLoadingInfo,
    isError: isErrorInfo,
  } = useQuery({
    queryKey: [CandidateExperienceQueryKeys.EMPLOYEE_INFO],
    queryFn: () => getEmployeeInfo(history),
  });
  const visibleTabs = useMemo(() => {
    return Object.values(EmployeePageTab).filter(
      (tab) => ENABLE_PAGES || tab !== EmployeePageTab.PAGES,
    );
  }, []);
  const [tabs] = useState(() => new Tabs(visibleTabs, history, false));

  const disabledTabs = useMemo((): DisabledTab[] => {
    if (!ENABLE_PAGES || !employeeInfo || hasPageScope(employeeInfo)) {
      return [];
    }
    return [{ label: EmployeePageTab.PAGES, tooltip: PAGES_TAB_DISABLED_TOOLTIP }];
  }, [employeeInfo]);

  useEffect(
    function updateTabTitle() {
      if (isLoadingInfo || !!isErrorInfo) {
        return;
      }
      document.title = 'Employee Dashboard';
    },
    [isLoadingInfo, isErrorInfo],
  );

  if (isLoadingInfo || !employeeInfo) {
    return <Typography variant="body">Loading...</Typography>;
  }
  if (isErrorInfo) {
    history.push('/error');
    return <Typography variant="body">Error</Typography>;
  }

  const contentMap = new Map<EmployeePageTab, () => JSX.Element>([
    [EmployeePageTab.OFFERS, (): JSX.Element => <OfferList />],
    ...(ENABLE_PAGES ? ([[EmployeePageTab.PAGES, (): JSX.Element => <PageList />]] as const) : []),
  ]);

  return (
    <Flex
      className="employee-page"
      padding="xxl"
      direction="column"
      justify="center"
      alignment="center"
      gap="lg"
    >
      <Header employeeName={`${employeeInfo.firstName} ${employeeInfo.lastName}`} />
      <Card
        className="content-container"
        direction="column"
        padding="md"
        gap="md"
        justify="center"
        alignment="center"
      >
        <TabbedPage tabs={tabs}>
          <TabBar disabledTabs={disabledTabs} />
          <TabWindow contentMap={contentMap} />
        </TabbedPage>
      </Card>
    </Flex>
  );
}
